import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import useFetchAssistantTodos from '../../api/useFetchAssistantTodos';
import useResetCareerBankLogin from '../../api/useResetCareerBankLogin';
import useSyncCareerBank from '../../api/useSyncCareerBank';
import AdminStatusBadge from '../../components/atoms/AdminStatusBadge';
import AdminErrorState from '../../components/molecules/AdminErrorState';
import AdminLoadingState from '../../components/molecules/AdminLoadingState';
import { IMPORT_REQUEST_STATUS_STYLES } from '../../constants/status';
import { formatDateTime } from '../../utils/formatters';

const cardClassName =
  'rounded border border-solid border-[#ccc] bg-white shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)] p-6';

const tableHeaderCellClassName =
  'bg-[#F3F7FE] border-0 border-b border-solid border-[#d9d9d9] px-4 py-3 text-left text-xs font-bold text-black';

const tableCellClassName =
  'border-0 border-b border-solid border-[#eee] px-4 py-3 text-sm text-[#333]';

const getCaptchaTarget = (targetType: string | null, targetId: number | null): string => {
  if (!targetType && targetId == null) return '-';
  if (!targetType) return `${targetId}`;
  if (targetId == null) return targetType;
  return `${targetType} #${targetId}`;
};

export default function AssistantTodosPage() {
  const { data, isLoading, isError } = useFetchAssistantTodos();
  const { mutate: syncCareerBank, isPending: isSyncPending } = useSyncCareerBank();
  const { mutate: resetCareerBankLogin, isPending: isResetPending } = useResetCareerBankLogin();
  const [syncSuccessMessage, setSyncSuccessMessage] = useState('');
  const [syncErrorMessage, setSyncErrorMessage] = useState('');

  if (isLoading) return <AdminLoadingState />;
  if (isError || !data)
    return <AdminErrorState message="アシスタントTODOの読み込みに失敗しました。" />;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <h1 className="my-0 text-2xl leading-[1.2] font-bold text-[#333]">アシスタントTODO</h1>

      <section className={cardClassName}>
        <h2 className="m-0 text-lg font-bold text-[#333]">
          📋 未処理クローリングリクエスト ({data.pending_import_requests.length}件)
        </h2>
        <div className="mt-4 overflow-x-auto">
          {data.pending_import_requests.length === 0 ? (
            <p className="m-0 text-sm text-[#666]">未処理のリクエストはありません ✅</p>
          ) : (
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr>
                  <th className={tableHeaderCellClassName}>クライアント名</th>
                  <th className={tableHeaderCellClassName}>ソース名</th>
                  <th className={tableHeaderCellClassName}>作成日</th>
                </tr>
              </thead>
              <tbody>
                {data.pending_import_requests.map((request) => (
                  <tr key={request.id} className="hover:bg-[#f9fbff]">
                    <td className={tableCellClassName}>
                      <Link
                        to="/admin"
                        search={{ tab: 'import_requests', importRequestId: request.id }}
                        className="block text-[#333] no-underline"
                      >
                        {request.organization_name ?? '-'}
                      </Link>
                    </td>
                    <td className={tableCellClassName}>
                      <Link
                        to="/admin"
                        search={{ tab: 'import_requests', importRequestId: request.id }}
                        className="flex items-center gap-2 text-[#333] no-underline"
                      >
                        <span>{request.job_source_title ?? '-'}</span>
                        <AdminStatusBadge
                          status={request.status}
                          statusMap={IMPORT_REQUEST_STATUS_STYLES}
                        />
                      </Link>
                    </td>
                    <td className={tableCellClassName}>
                      <Link
                        to="/admin"
                        search={{ tab: 'import_requests', importRequestId: request.id }}
                        className="block text-[#333] no-underline"
                      >
                        {formatDateTime(request.created_at)}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className={cardClassName}>
        <h2 className="m-0 text-lg font-bold text-[#333]">
          🔐 CAPTCHA回答待ち ({data.pending_captcha_challenges.length}件)
        </h2>
        <div className="mt-4 overflow-x-auto">
          {data.pending_captcha_challenges.length === 0 ? (
            <p className="m-0 text-sm text-[#666]">回答待ちのCAPTCHAはありません ✅</p>
          ) : (
            <table className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr>
                  <th className={tableHeaderCellClassName}>対象</th>
                  <th className={tableHeaderCellClassName}>画像URL</th>
                  <th className={tableHeaderCellClassName}>期限</th>
                  <th className={tableHeaderCellClassName}>作成日</th>
                </tr>
              </thead>
              <tbody>
                {data.pending_captcha_challenges.map((challenge) => (
                  <tr key={challenge.id}>
                    <td className={tableCellClassName}>
                      {getCaptchaTarget(challenge.target_type, challenge.target_id)}
                    </td>
                    <td className={tableCellClassName}>
                      <a
                        href={challenge.image_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#4188ff]"
                      >
                        画像を開く
                      </a>
                    </td>
                    <td className={tableCellClassName}>{formatDateTime(challenge.expired_at)}</td>
                    <td className={tableCellClassName}>{formatDateTime(challenge.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className={cardClassName}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="m-0 text-lg font-bold text-[#333]">🔄 Career Bank同期</h2>
          <button
            type="button"
            onClick={() => {
              setSyncSuccessMessage('');
              setSyncErrorMessage('');
              syncCareerBank(undefined, {
                onSuccess: () => {
                  setSyncSuccessMessage(
                    'ログイン確認をキューに登録しました。届いたCAPTCHAに回答してください'
                  );
                },
                onError: (error) => {
                  setSyncErrorMessage(
                    error instanceof Error ? error.message : 'Career Bank同期の開始に失敗しました'
                  );
                },
              });
            }}
            disabled={isSyncPending}
            className="rounded border-none bg-[#4188FF] px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
          >
            同期を開始する
          </button>
        </div>

        <div className="mt-3 min-h-6">
          {isSyncPending && (
            <p className="m-0 text-sm font-bold text-[#1f5fbf]">同期ジョブを送信中...</p>
          )}
          {!isSyncPending && syncSuccessMessage && (
            <p className="m-0 text-sm font-bold text-[#1f7a52]">{syncSuccessMessage}</p>
          )}
          {!isSyncPending && syncErrorMessage && (
            <p className="m-0 text-sm font-bold text-[#c62828]">{syncErrorMessage}</p>
          )}
        </div>

        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[840px] border-collapse">
            <thead>
              <tr>
                <th className={tableHeaderCellClassName}>組織名</th>
                <th className={tableHeaderCellClassName}>ログインID</th>
                <th className={tableHeaderCellClassName}>ログインステータス</th>
                <th className={tableHeaderCellClassName}>最終クロールステータス</th>
                <th className={tableHeaderCellClassName}>最終更新日</th>
                <th className={tableHeaderCellClassName}>
                  <span className="sr-only">操作</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.career_bank_sources.length === 0 ? (
                <tr>
                  <td colSpan={6} className={tableCellClassName}>
                    Career Bankソースはありません
                  </td>
                </tr>
              ) : (
                data.career_bank_sources.map((source) => (
                  <tr key={source.id}>
                    <td className={tableCellClassName}>{source.organization_name ?? '-'}</td>
                    <td className={tableCellClassName}>{source.login_id ?? '-'}</td>
                    <td className={tableCellClassName}>{source.login_status ?? '-'}</td>
                    <td className={tableCellClassName}>{source.last_crawl_status ?? '-'}</td>
                    <td className={tableCellClassName}>{formatDateTime(source.last_crawled_at)}</td>
                    <td className={tableCellClassName}>
                      {source.login_status === 'failed' && (
                        <button
                          type="button"
                          onClick={() => {
                            setSyncSuccessMessage('');
                            setSyncErrorMessage('');
                            resetCareerBankLogin(source.id, {
                              onSuccess: () => setSyncSuccessMessage('同期対象に戻しました'),
                              onError: (error) =>
                                setSyncErrorMessage(
                                  error instanceof Error
                                    ? error.message
                                    : '同期対象に戻せませんでした'
                                ),
                            });
                          }}
                          disabled={isResetPending}
                          className="rounded border border-[#4188FF] bg-white px-3 py-1 text-xs font-bold whitespace-nowrap text-[#4188FF] disabled:opacity-60"
                        >
                          同期対象に戻す
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

if (import.meta.hot) {
  import.meta.hot.accept((newModule) => {
    if (newModule) import.meta.hot?.invalidate();
  });
}
